import { Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy, Input, ViewChildren, QueryList, AfterViewInit, ElementRef } from '@angular/core';
import { AfterpayDialogComponent } from 'src/@omnial/shared/afterpay-dialog/afterpay-dialog.component';
import { fadeInLeftAnimation } from 'src/@omnial/_animations/fade-in-left.animation';
import { fadeInRightAnimation } from 'src/@omnial/_animations/fade-in-right.animation';
import { fadeInUpAnimation } from 'src/@omnial/_animations/fade-in-up.animation';
import { scaleInAnimation } from 'src/@omnial/_animations/scale-in.animation';
import { ProductDetail, Product, ProductSpecification } from 'src/@omnial/_models/catalog/product.model';
import { UrlRecord } from 'src/@omnial/_models/catalog/url-record.model';
import { Customer } from 'src/@omnial/_models/customer/customer.model';
import { AfterpayLimits } from 'src/@omnial/_models/external/afterpay-config.model';
import { SiteMapItem } from 'src/@omnial/_models/navigation/site-map.model';
import { AfterpayService } from 'src/@omnial/_payments/afterpay.service';
import { CatalogService } from 'src/@omnial/_services/catalog/catalog.service';
import { CustomerService } from 'src/@omnial/_services/customer/customer.service';
import { CanonicalService } from 'src/@omnial/_services/navigation/canonical.service';
import { SiteMapService } from 'src/@omnial/_services/navigation/sitemap.service';
import { ThemeService } from 'src/@omnial/_services/catalog/theme.service';
import { TopicService } from 'src/@omnial/_services/catalog/topic.service';
import { Category } from 'src/@omnial/_models/catalog/category.model';
import { Topic } from 'src/@omnial/_models/catalog/topic.model';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatDialog } from '@angular/material/dialog';
import { ProductSwiperComponent } from 'src/@omnial/pages/product/product-swiper/product-swiper.component';
import { ProductReviewComponent } from 'src/@omnial/shared/product-review/product-review.component';
import { DealerLocatorComponent } from 'src/@omnial/shared/dealer-locator/dealer-locator.component';
import { AppSettings } from 'src/app/app.settings';
import { Manufacturer } from 'src/@omnial/_models/catalog/manufacturer.model';
import { RatingComponent } from 'src/@omnial/shared/rating/rating.component';
import { ControlsMiniComponent } from 'src/@omnial/product-controls/controls-mini/controls-mini.component';
import { ControlsDetailComponent } from 'src/@omnial/product-controls/controls-detail/controls-detail.component';

@Component({
    selector: 'app-product-detail',
    templateUrl: './../../../../app/pages/product/product-detail/product-detail.component.html',
    styleUrls: ['./../../../../app/pages/product/product-detail/product-detail.component.scss'],
    animations: [fadeInUpAnimation, fadeInRightAnimation, fadeInLeftAnimation, scaleInAnimation],
    standalone: false
})

export class ProductDetailComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input() currentProduct: ProductDetail;
  @Input() currentCategory: Category;
  @Input() currentManufacturer: Manufacturer;
  @Input() quickView: boolean;
  @ViewChildren('expandedProductText') expandedProductText: QueryList<ElementRef>;
  @ViewChildren('rating') rating: QueryList<RatingComponent>;
  @ViewChildren('controlsMini') controlsMini: QueryList<ControlsMiniComponent>;
  @ViewChildren('controlsDetail') controlsDetail: QueryList<ControlsDetailComponent>;
  @ViewChildren('productSwiper') productSwipers: QueryList<ProductSwiperComponent>;
  @ViewChildren('productReview') productReviews: QueryList<ProductReviewComponent>;
  @ViewChildren('dealerLocator') dealerLocators: QueryList<DealerLocatorComponent>;
  public smallImage = false;
  public expandedText = true;
  public needsExpand = false;
  public afterpayLimits: AfterpayLimits;
  public productWaiting = true;
  public customer: Customer;
  public urlRecord: UrlRecord;
  public productFail = false;
  public relatedProducts: Array<Product>;
  public sitemapItems: SiteMapItem[];
  public flexGtMd = 78;
  public flexMd = 74;
  public afterpayPop = false;
  public productAvailable = true;
  public tabIndex = 0;
  public subscriptions: Subscription[] = [];

  constructor(
    public afterpayService: AfterpayService,
    public dialog: MatDialog,
    public catalogService: CatalogService,
    public topicService: TopicService,
    public sitemapService: SiteMapService,
    public canonicalService: CanonicalService,
    public themeService: ThemeService,
    public customerService: CustomerService,
    public settings: AppSettings,
    public spinner: NgxSpinnerService) {
    this.subscriptions.push(this.afterpayService.limits.subscribe(res => { this.afterpayLimits = res as AfterpayLimits; }));
    this.subscriptions.push(this.customerService.customer.subscribe(res => { this.customer = res as Customer; }));
  }

  ngAfterViewInit(): void {
    this.setChildControls();
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => { sub.unsubscribe(); });
    }
  }

  ngOnInit(): void {
    if (this.currentProduct) {
      this.spinner.hide();
      this.initProduct();
    } else {
      this.productWaiting = false;
      this.productFail = true;
    }
  }

  initProduct(product?: ProductDetail) {
    if (product) {
      this.currentProduct = product;
      this.setChildControls();
    }
    this.productWaiting = false;
    this.productFail = false;
    this.spinner.hide();
    if (this.currentProduct.stockQuantity > 0) {
      this.productAvailable = true;
    } else {
      this.productAvailable = false;
    }
    this.productLayout();
    setTimeout(() => {
      this.needsExpand = this.needsToExpand(this.settings.productDetailExpandHeightLimit);
      this.expandedText = !this.needsExpand });
  }

  needsToExpand(minHeight: number): boolean {
    let showExpand = false;
    this.expandedProductText?.forEach((text) => {
      if (text.nativeElement.offsetHeight > minHeight) {
        showExpand = true;
      }
    });
    return showExpand;
  }

  public setChildControls(): void {
    if (this.currentProduct) {
      this.rating?.forEach((rating) => {
        rating.init(this.currentProduct);
      });
      this.controlsMini?.forEach((controlMini) => {
        controlMini.init(this.currentProduct);
      });
      this.controlsDetail?.forEach((controlDetail) => {
        controlDetail.init(this.currentProduct);
      });
      this.productSwipers?.forEach((productSwiper) => {
        productSwiper.initSwiper(this.currentProduct);
      });
      this.productReviews?.forEach((productReview) => {
        productReview.init(this.currentProduct);
      });
      this.dealerLocators?.forEach((dealerLocator) => {
        dealerLocator.init(this.currentProduct);
      });
    }
  }

  public productLayout(): void {
    this.subscriptions.push(this.sitemapService.siteMap.subscribe(
      res => {
        this.sitemapItems = res as SiteMapItem[];
        this.flexGtMd = 78;
        this.flexMd = 74;
      }));
  }

  public openAfterpay(): void {
    this.subscriptions.push(this.topicService.getTopicBySlug('afterpay').subscribe(
      res => {
        const topic = res as Topic;
        if (topic) {
          this.dialog.open(AfterpayDialogComponent, {
            data: { product: this.currentProduct, content: topic.body },
            autoFocus: false,
            maxHeight: '90vh',
            maxWidth: '90%'
          });
        }
      }));
  }

  public swapProduct(productId: number): void {
    // This is called for attribute changes so the correct Product Detail is shown
    this.spinner.show();
    this.subscriptions.push(this.catalogService.getProductDetail(productId).subscribe(
      res => {
        this.spinner.hide();
        const attributeProduct = res as ProductDetail;
        if (attributeProduct) {
          this.currentProduct.images = attributeProduct.images;
          this.currentProduct.shortDescription = attributeProduct.shortDescription;
          this.currentProduct.fullDescription = attributeProduct.fullDescription;
        }
      }));
  }

  public specification(name: string): ProductSpecification {
    if (this.currentProduct?.specifications) {
      return this.currentProduct.specifications.find(s => s.specificationAttributeName === name);
    }
    return null;
  }

  public scrollTitle(): void {
    setTimeout(() => {
      const reviewContent = document.getElementById('pdp-name');
      if (reviewContent?.offsetTop) {
        window.scrollTo({ top: reviewContent.offsetTop - 20, behavior: 'smooth' });
      }
    }, 10);
  }

  public scrollReviews(): void {
    setTimeout(() => {
      const reviewContent = document.getElementById('productReviews');
      if (reviewContent?.offsetTop) {
        window.scrollTo({ top: reviewContent.offsetTop - 20, behavior: 'smooth' });
      }
    }, 500);
  }
}
